/**
 * Shared theme utilities, mapped to the @thegpvc/ui design tokens
 * (navy = structural/text, cream = page background, orange = primary/links,
 * gray = borders). Use these instead of hardcoding color classes so the app
 * stays aligned with the gp-ui palette in both light and dark mode.
 */

// Text colors
export const text = {
  primary: 'text-navy-900 dark:text-white',
  secondary: 'text-navy-600 dark:text-navy-300',
  muted: 'text-navy-400 dark:text-navy-500',
  error: 'text-rose-600 dark:text-rose-400',
  success: 'text-emerald-600 dark:text-emerald-400',
  link: 'text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300',
};

// Background colors
export const bg = {
  page: 'bg-cream dark:bg-navy-950',
  surface: 'bg-navy-50 dark:bg-navy-800',
  surfaceHover: 'hover:bg-navy-50 dark:hover:bg-navy-800',
  muted: 'bg-navy-100 dark:bg-navy-700',
};

// Border colors
export const border = {
  default: 'border-gray-200 dark:border-navy-700',
  muted: 'border-gray-100 dark:border-navy-800',
};

// Table styles
export const table = {
  header: 'bg-navy-50 dark:bg-navy-800',
  headerText: 'text-navy-500 dark:text-navy-300',
  row: 'bg-white dark:bg-navy-900',
  rowHover: 'hover:bg-navy-50 dark:hover:bg-navy-800',
  divide: 'divide-gray-200 dark:divide-navy-700',
  divideMuted: 'divide-gray-100 dark:divide-navy-800',
};

// Page-shell layout tokens — the two canonical content-shell widths every app
// page uses via the `PageContainer` component. `pageWide` for overview / list /
// table pages, `pageNarrow` for reading / detail pages. Both center the content
// (`mx-auto`), fill available width up to their max, and share the same vertical
// rhythm and responsive padding. See docs/architecture/dashboard-style-guide.md.
export const layout = {
  pageWide: 'mx-auto w-full max-w-7xl space-y-6 p-6 lg:p-8',
  pageNarrow: 'mx-auto w-full max-w-5xl space-y-6 p-6 lg:p-8',
};

// Fixed column-width tokens — the canonical scale for in-component label / value
// / input columns (as opposed to the page-shell `layout` widths above). Fold
// ad-hoc fixed widths to the *nearest* tier rather than inventing new sizes; for
// columns that must not shrink, apply the same numeric scale with the `min-w-`
// variant (e.g. `min-w-32`). See docs/architecture/dashboard-style-guide.md.
export const colWidth = {
  labelSm: 'w-24', // short labels, tool names, ranks
  labelMd: 'w-32', // standard label columns
  valueSm: 'w-12', // short numeric values
  valueMd: 'w-16', // durations / counts
  inputSm: 'w-28', // small form inputs
  inputMd: 'w-40', // medium form inputs
  inputLg: 'w-64', // wide form inputs
};

// Page title (H1: 26px bold per gp-ui type scale)
export const pageTitle = `text-2xl font-bold ${text.primary}`;

// Section title (H3: 16px semibold)
export const sectionTitle = `text-base font-semibold ${text.primary}`;

// Helper to combine classes
export function cx(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}
